<template>
  <div>
    <div class="admin-toolbar">
      <div class="inline-flex rounded-md border border-gray-200 bg-white p-1 shadow-sm">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="currentTab = tab.id"
          :class="['rounded px-3 py-1.5 text-sm font-semibold transition-colors', currentTab === tab.id ? 'bg-brand-red text-white' : 'text-gray-600 hover:bg-gray-100']"
        >
          {{ tab.label }} <span class="ml-0.5 opacity-70 tabular-nums">({{ tab.count() }})</span>
        </button>
      </div>
      <div class="flex items-center gap-3">
        <span v-if="pendingCount" class="text-sm font-semibold text-amber-700">{{ pendingCount }} waiting for approval</span>
        <button @click="showCreateModal = true" class="btn-primary">
          <i class="fa-solid fa-star text-xs"></i> Add Renowned Artist
        </button>
      </div>
    </div>

    <div v-if="loading" class="admin-empty">Loading artists...</div>
    <div v-else-if="error" class="admin-empty text-red-600">{{ error }}</div>
    <div v-else-if="filteredArtists.length === 0" class="admin-empty">
      <i class="fa-solid fa-palette text-2xl text-gray-300"></i>
      No artists found.
    </div>

    <div v-else class="admin-table-wrap">
      <table class="admin-table">
        <thead>
          <tr>
            <th>Artist</th>
            <th>Section</th>
            <th>Status</th>
            <th>Visibility</th>
            <th>Description</th>
            <th>Achievements</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="artist in filteredArtists" :key="artist.id">
            <td>
              <div class="flex items-center gap-3">
                <img
                  :src="artist.image"
                  alt="Artist image"
                  class="h-12 w-12 flex-shrink-0 rounded-md border border-gray-200 object-cover"
                  @error="handleImageError($event, artist)"
                />
                <div class="min-w-0">
                  <p class="font-medium text-gray-900">{{ artist.name || 'N/A' }}</p>
                  <p class="text-xs text-gray-500">{{ artist.profession }}</p>
                </div>
              </div>
            </td>
            <td>
              <span v-if="artist.is_renowned" class="badge bg-brand-cream/70 text-brand-red-dark">
                <i class="fa-solid fa-star text-[10px] text-amber-500"></i> Renowned
              </span>
              <span v-else class="badge bg-gray-100 text-gray-600">Artist Profile</span>
            </td>
            <td>
              <span
                :class="artist.is_approved ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'"
                class="badge"
              >
                {{ artist.is_approved ? 'Approved' : 'Pending' }}
              </span>
            </td>
            <!-- Visibility badge — only meaningful for approved artists -->
            <td>
              <span v-if="artist.is_approved"
                :class="artist.is_hidden ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-700'"
                class="badge"
              >
                <i :class="['fa-solid', artist.is_hidden ? 'fa-eye-slash' : 'fa-eye']"></i>
                {{ artist.is_hidden ? 'Hidden' : 'Visible' }}
              </span>
              <span v-else class="text-xs text-gray-400">—</span>
            </td>
            <td class="max-w-xs">{{ truncateContent(artist.description) }}</td>
            <td class="max-w-xs">{{ truncateContent(artist.achievements) }}</td>
            <td>
              <div class="flex flex-wrap justify-end gap-2">
                <button @click="viewArtist(artist)" class="btn-ghost btn-sm">View</button>
                <button v-if="!artist.is_approved" @click="approveArtist(artist.id)" class="btn-success btn-sm">Approve</button>
                <!-- Hide/Show only for approved artists -->
                <button v-if="artist.is_approved" @click="toggleVisibility(artist)" class="btn-ghost btn-sm">
                  {{ artist.is_hidden ? 'Show' : 'Hide' }}
                </button>
                <button @click="toggleRenowned(artist)" class="btn-ghost btn-sm" :title="artist.is_renowned ? 'Move to Artist Profiles' : 'Move to Renowned Artists'">
                  <i :class="['fa-star text-[10px]', artist.is_renowned ? 'fa-solid text-amber-500' : 'fa-regular']"></i>
                  {{ artist.is_renowned ? 'Unmark' : 'Renowned' }}
                </button>
                <button @click="deleteArtist(artist.id)" class="btn-danger btn-sm">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- View Artist Modal -->
    <Teleport to="body">
      <div
        v-if="showViewModal && selectedArtist"
        class="modal-backdrop"
        @click.self="closeViewModal"
      >
        <div class="modal-panel max-w-2xl">
          <button
            @click="closeViewModal"
            class="absolute right-4 top-4 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>

          <div class="flex flex-col gap-6 md:flex-row">
            <!-- Image -->
            <div class="w-full flex-shrink-0 md:w-1/3">
              <img
                :src="selectedArtist.image"
                alt="Artist"
                class="aspect-square w-full rounded-md object-cover shadow-sm"
                @error="handleImageError($event, selectedArtist)"
              />
              <div class="mt-3 text-center">
                <span
                  :class="selectedArtist.is_approved ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'"
                  class="badge"
                >
                  {{ selectedArtist.is_approved ? 'Approved' : 'Pending Approval' }}
                </span>
              </div>
            </div>

            <!-- Details -->
            <div class="w-full space-y-4 md:w-2/3">
              <div>
                <h2 class="font-serif text-2xl font-bold text-gray-900">{{ selectedArtist.name }}</h2>
                <p class="text-sm text-gray-500">{{ selectedArtist.profession }} · ID {{ selectedArtist.id }}</p>
              </div>

              <div>
                <h3 class="form-label">Description</h3>
                <p class="whitespace-pre-wrap text-sm text-gray-600">{{ selectedArtist.description || 'N/A' }}</p>
              </div>

              <div v-if="selectedArtist.achievements">
                <h3 class="form-label">Achievements</h3>
                <p class="whitespace-pre-wrap text-sm text-gray-600">{{ selectedArtist.achievements }}</p>
              </div>

              <router-link :to="`/profile/${selectedArtist.id}`" target="_blank" class="link-arrow">
                <span>Open public profile</span>
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </router-link>
            </div>
          </div>

          <!-- Footer Actions -->
          <div class="mt-8 flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-5">
            <button @click="closeViewModal" class="btn-ghost">Close</button>

            <button
              v-if="!selectedArtist.is_approved"
              @click="approveArtist(selectedArtist.id)"
              class="btn-success"
            >
              <i class="fa-solid fa-check"></i> Approve Artist
            </button>

            <!-- Hide / Show toggle — only for approved artists -->
            <button
              v-if="selectedArtist.is_approved"
              @click="toggleVisibility(selectedArtist)"
              class="btn-ghost"
            >
              <i :class="['fa-solid', selectedArtist.is_hidden ? 'fa-eye' : 'fa-eye-slash']"></i>
              {{ selectedArtist.is_hidden ? 'Make Visible' : 'Hide Profile' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Add Renowned Artist (same form artists use, published by the admin) -->
    <Teleport to="body">
      <div v-if="showCreateModal" class="modal-backdrop items-start" @click.self="showCreateModal = false">
        <div class="relative my-8 w-full max-w-4xl rounded-md bg-white">
          <button
            @click="showCreateModal = false"
            class="absolute right-4 top-4 z-10 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
          <ArtistCreateForm renowned @created="onRenownedCreated" @cancel="showCreateModal = false" />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Swal from 'sweetalert2'
import api, { errorMessage, FILE_BASE_URL } from '@/api'
import ArtistCreateForm from './ArtistCreateForm.vue'

const DEFAULT_IMAGE = `${FILE_BASE_URL}/images/default.png`

const artists = ref([])
const loading = ref(false)
const error = ref(null)

const pendingCount = computed(() => artists.value.filter(a => !a.is_approved).length)

// Tabs: all / renowned (added by admin) / artist profiles (self-published)
const currentTab = ref('all')
const tabs = [
  { id: 'all', label: 'All', count: () => artists.value.length },
  { id: 'renowned', label: 'Renowned', count: () => artists.value.filter(a => a.is_renowned).length },
  { id: 'profiles', label: 'Artist Profiles', count: () => artists.value.filter(a => !a.is_renowned).length },
]
const filteredArtists = computed(() => {
  if (currentTab.value === 'renowned') return artists.value.filter(a => a.is_renowned)
  if (currentTab.value === 'profiles') return artists.value.filter(a => !a.is_renowned)
  return artists.value
})

// "Add Renowned Artist" modal
const showCreateModal = ref(false)
const onRenownedCreated = () => {
  showCreateModal.value = false
  currentTab.value = 'renowned'
  fetchArtists()
}

// Move an artist into / out of the Renowned section
const toggleRenowned = async (artist) => {
  const toRenowned = !artist.is_renowned
  const result = await Swal.fire({
    title: toRenowned ? 'Mark as Renowned Artist?' : 'Move to Artist Profiles?',
    text: toRenowned
      ? `"${artist.name}" will appear in the Renowned Artist section.`
      : `"${artist.name}" will move back to the Artist Profile section.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#D84040',
    cancelButtonColor: '#6B7280',
    confirmButtonText: 'Yes, move it'
  })
  if (!result.isConfirmed) return

  try {
    const { data } = await api.put(`/artists/${artist.id}/toggle-renowned`)
    artist.is_renowned = data.is_renowned
    if (data.is_renowned) artist.is_approved = 1
    Swal.fire({ icon: 'success', title: data.message, timer: 1600, showConfirmButton: false })
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to update artist'), confirmButtonColor: '#BF3131' })
  }
}

const fetchArtists = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await api.get('/admin/artists')
    artists.value = response.data.map(artist => {
      let imageUrl = DEFAULT_IMAGE
      if (artist.image) {
        imageUrl = artist.image.startsWith('data:') || artist.image.startsWith('http')
          ? artist.image
          : `${FILE_BASE_URL}${artist.image}`
      }
      return { ...artist, image: imageUrl }
    })
  } catch (err) {
    error.value = errorMessage(err, 'Failed to fetch artists')
  } finally {
    loading.value = false
  }
}

const handleImageError = (event, artist) => {
  if (event.target.src === DEFAULT_IMAGE) return
  event.target.src = DEFAULT_IMAGE
  artist.image = DEFAULT_IMAGE
}

// View Modal State
const showViewModal = ref(false)
const selectedArtist = ref(null)

const truncateContent = content => {
  if (!content) return 'N/A'
  return content.length > 60 ? content.substring(0, 60) + '...' : content
}

const viewArtist = artist => {
  selectedArtist.value = artist
  showViewModal.value = true
}

const closeViewModal = () => {
  showViewModal.value = false
  selectedArtist.value = null
}

// Approve artist
const approveArtist = async id => {
  const result = await Swal.fire({
    title: 'Approve Artist?',
    text: "Are you sure you want to approve this artist?",
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#059669',
    cancelButtonColor: '#6B7280',
    confirmButtonText: 'Yes, approve!'
  });

  if (!result.isConfirmed) return;

  try {
    await api.put(`/artists/${id}/approve`)

    const artist = artists.value.find(a => a.id === id)
    if (artist) artist.is_approved = 1
    if (selectedArtist.value && selectedArtist.value.id === id) {
      selectedArtist.value.is_approved = 1
    }

    Swal.fire({ icon: 'success', title: 'Approved!', text: 'Artist approved successfully!', timer: 1800, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to approve artist'), confirmButtonColor: '#BF3131' });
  }
}

// Hide / Show artist profile
const toggleVisibility = async (artist) => {
  const action = artist.is_hidden ? 'make visible' : 'hide';
  const result = await Swal.fire({
    title: `${artist.is_hidden ? 'Show' : 'Hide'} Profile?`,
    text: `Are you sure you want to ${action} "${artist.name}"?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: artist.is_hidden ? '#059669' : '#4B5563',
    cancelButtonColor: '#6B7280',
    confirmButtonText: `Yes, ${action}!`
  });

  if (!result.isConfirmed) return;

  try {
    const { data } = await api.put(`/artists/${artist.id}/toggle-visibility`)

    const listed = artists.value.find(a => a.id === artist.id)
    if (listed) listed.is_hidden = data.is_hidden
    if (selectedArtist.value && selectedArtist.value.id === artist.id) {
      selectedArtist.value.is_hidden = data.is_hidden
    }

    Swal.fire({
      icon: 'success',
      title: data.is_hidden ? 'Profile Hidden' : 'Profile Visible',
      text: data.message,
      timer: 1800,
      showConfirmButton: false
    });
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to toggle visibility'), confirmButtonColor: '#BF3131' });
  }
}

// Delete artist
const deleteArtist = async id => {
  const result = await Swal.fire({
    title: 'Delete Artist?',
    text: "This removes the profile, its gallery, links, likes and comments. You won't be able to revert this!",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#DC2626',
    cancelButtonColor: '#6B7280',
    confirmButtonText: 'Yes, delete it!'
  });

  if (!result.isConfirmed) return;

  try {
    await api.delete(`/artists/${id}`)
    artists.value = artists.value.filter(a => a.id !== id)
    if (selectedArtist.value && selectedArtist.value.id === id) closeViewModal()

    Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Artist deleted successfully!', timer: 1800, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to delete artist'), confirmButtonColor: '#BF3131' });
  }
}

onMounted(fetchArtists)
</script>
